import CircularProgress from '@/global/components/CircularProgress'
import { funcionario as funcionario_api } from '@/service/api'
import { FormFile, FormFileHandle } from 'global/components/Form'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { FrownIcon } from 'lucide-react'
import { useFuncionariosOutletContext } from 'pages/Funcionarios.context'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AvisoFotoFacial } from './AvisoFotoFacial'

export default function FacialModal() {
    const navigate = useNavigate()
    const { data: funcionarios, refetch } = useFuncionariosOutletContext()
    const { funcionarioId } = useParams()
    const funcionario = funcionarios?.find(
        item => item.id === Number(funcionarioId),
    )

    const [fotoUrl, setFotoUrl] = useState<string | null>(null)
    const [isLoadingImage, setIsLoadingImage] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isAvisoAberto, setIsAvisoAberto] = useState(false)
    const campoFotoRef = useRef<FormFileHandle>(null)

    useEffect(() => {
        let atual: string | null = null

        funcionario_api.foto
            .fetchUrl(Number(funcionarioId))
            .then(url => {
                atual = url
                setFotoUrl(url)
            })
            .catch(err =>
                toast.error(
                    err?.message ?? 'Não foi possível carregar a foto.',
                ),
            )
            .finally(() => setIsLoadingImage(false))

        return () => {
            if (atual) URL.revokeObjectURL(atual)
        }
    }, [funcionarioId, funcionario?.fotoFilename])

    const handleSubmit = useCallback(
        async ({ rosto }: { rosto: File }) => {
            setIsSaving(true)

            try {
                await funcionario_api.foto.upload(Number(funcionarioId), rosto)
                toast.success('Foto cadastrada com sucesso.')

                await refetch?.()
                navigate(-1)
            } catch (err: any) {
                toast.error(err?.message ?? 'Não foi possível salvar a foto.')
            } finally {
                setIsSaving(false)
            }
        },
        [funcionarioId, navigate, refetch],
    )

    const handleRemove = useCallback(async () => {
        setIsSaving(true)

        try {
            await funcionario_api.foto.remove(Number(funcionarioId))
            toast.success('Foto removida.')
            await refetch?.()
            navigate(-1)
        } catch (err: any) {
            toast.error(err?.message ?? 'Não foi possível remover a foto.')
        } finally {
            setIsSaving(false)
        }
    }, [funcionarioId, navigate, refetch])

    if (!funcionario) return null

    return (
        <Dialog.Root
            open={hasPermission(['update', 'Funcionários'])}
            onSubmit={handleSubmit}
            width="lg"
            loading={isSaving}
        >
            <Dialog.Title>Foto do funcionário</Dialog.Title>
            <Dialog.Content className="flex justify-center flex-col">
                <FormFile
                    ref={campoFotoRef}
                    name="rosto"
                    label={fotoUrl ? 'Alterar a foto' : 'Cadastrar foto'}
                    onBeforeOpen={() => {
                        setIsAvisoAberto(true)
                        return false
                    }}
                />
                <AvisoFotoFacial
                    open={isAvisoAberto}
                    onCancel={() => setIsAvisoAberto(false)}
                    onConfirm={() => {
                        setIsAvisoAberto(false)
                        campoFotoRef.current?.abrirSeletor()
                    }}
                />
                <div className="flex flex-col items-center justify-center h-90 my-8">
                    <ImagePreview
                        fotoUrl={fotoUrl}
                        isLoadingImage={isLoadingImage}
                        nome={funcionario.nome}
                    />
                </div>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {!!fotoUrl && (
                    <Dialog.Actions.PrimaryButton
                        color="error"
                        onClick={handleRemove}
                    >
                        Remover
                    </Dialog.Actions.PrimaryButton>
                )}
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

interface ImagePreviewProps {
    fotoUrl: string | null
    isLoadingImage: boolean
    nome: string
}

function ImagePreview({ fotoUrl, isLoadingImage, nome }: ImagePreviewProps) {
    const { watch } = useFormContext()
    const [previewImage, setPreviewImage] = useState<string | null>(null)
    const rosto = watch('rosto')

    useEffect(() => {
        if (rosto && rosto instanceof File) {
            const reader = new FileReader()
            reader.onload = e => setPreviewImage(e.target?.result as string)
            reader.readAsDataURL(rosto)
        } else if (!rosto) {
            setPreviewImage(null)
        }
    }, [rosto])

    if (isLoadingImage) return <CircularProgress />

    if (previewImage)
        return (
            <img
                alt="Preview da imagem selecionada"
                src={previewImage}
                className="rounded-sm h-96"
            />
        )

    if (fotoUrl)
        return <img alt={nome} src={fotoUrl} className="rounded-sm h-96" />

    return (
        <div className="flex flex-col items-center gap-4">
            <FrownIcon className="size-12" />
            <span>Nenhuma foto cadastrada</span>
        </div>
    )
}
